import { Children, Suspense, isValidElement, lazy, useEffect, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.ts';
// Le contenu affiché doit correspondre exactement à ce qui est déployé dans CETTE image —
// pas un fetch runtime vers une source qui pourrait avoir changé depuis. D'où l'import brut au
// build plutôt qu'un appel API ou GitLab Releases : le changelog est versionné avec le code.
import changelogRaw from '../../../../CHANGELOG.md?raw';

// Remplace l'emoji 🔔 d'origine — détonnait avec le reste de l'appli, qui n'utilise que des
// icônes SVG "trait" maison (voir CommitContextMenu.tsx : viewBox 16x16, stroke currentColor,
// pas de fill). currentColor hérite directement de la couleur du bouton (.whats-new-trigger),
// donc le hover fonctionne sans classe dédiée.
// react-markdown (et l'écosystème remark qu'il embarque) n'est utile qu'une fois le tiroir ouvert : chargé à
// la demande plutôt que dans le bundle initial (GU-358).
const ReactMarkdown = lazy(() => import('react-markdown'));

function MegaphoneIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M3 6 L3 9 L12 12.5 L12 2.5 Z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
      <rect x="1.6" y="9" width="1.4" height="2.6" rx="0.4" stroke="currentColor" strokeWidth="1.3" />
      <path d="M13 3 L14.6 1.8 M13 12 L14.6 13.2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}

// Convention Keep a Changelog : la version la plus récente est le premier "## " du fichier.
const latestVersion = changelogRaw.match(/^## (\S+)/m)?.[1] ?? null;

// Titre de section "0.7.0 — 2026-09-07" (voir CHANGELOG.md) : la date d'origine est un ISO brut,
// peu lisible en survol rapide du drawer. Le groupe date est optionnel — une entrée plus ancienne
// sans date (avant l'ajout de cette convention) retombe simplement sur le rendu <h2> par défaut.
const VERSION_HEADING_RE = /^(\S+)(?:\s+—\s+(\d{4}-\d{2}-\d{2}))?$/;

// react-markdown peut découper le texte d'un heading en plusieurs enfants (nœuds texte,
// éléments inline) — on les aplatit en une seule chaîne avant de tenter le match ci-dessus plutôt
// que de supposer un enfant unique.
function headingText(children: ReactNode): string {
  return Children.toArray(children)
    .map((child) => {
      if (typeof child === 'string' || typeof child === 'number') return String(child);
      if (isValidElement<{ children?: ReactNode }>(child)) return headingText(child.props.children);
      return '';
    })
    .join('');
}

function formatReleaseDate(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
}

function VersionHeading({ children }: { children?: ReactNode }) {
  const match = VERSION_HEADING_RE.exec(headingText(children));
  const version = match?.[1];
  const iso = match?.[2];
  if (!version || !iso) return <h2>{children}</h2>;
  return (
    <h2 className="whats-new-version-heading">
      <span className="whats-new-version-number">{version}</span>
      <span className="whats-new-version-date">{formatReleaseDate(iso)}</span>
    </h2>
  );
}

export function WhatsNewTrigger() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);

  // Préférence par compte (routes/preferences.ts), plus seulement par navigateur — même clé de
  // cache ['preferences'] que SettingsModal.tsx/RepoViewPage.tsx, déjà préchauffée par App.tsx.
  const preferencesQuery = useQuery({
    queryKey: ['preferences'],
    queryFn: () => api.getPreferences(),
  });
  const seenVersion = preferencesQuery.data?.seenChangelogVersion ?? null;
  // undefined tant que preferencesQuery n'a pas résolu : traité comme "rien à signaler" plutôt
  // que d'afficher le badge puis le faire disparaître une fois la vraie valeur connue (flash).
  const unseen = preferencesQuery.data !== undefined && latestVersion !== null && seenVersion !== latestVersion;

  const markSeenMutation = useMutation({
    mutationFn: (version: string) => api.updatePreferences({ seenChangelogVersion: version }),
    onSuccess: (updated) => qc.setQueryData(['preferences'], updated),
  });

  // Ferme aussi via Échap, comme annoncé par le commentaire ci-dessous — le backdrop seul
  // (onClick) ne peut pas recevoir cette touche : il n'est qu'un frère du panneau du drawer
  // dans le DOM, pas un ancêtre, donc rien n'y bulle depuis le contenu focalisé.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  return (
    <>
      {/* Ouvrir ne marque plus lu — seul le bouton "Marquer comme lu" du pied du drawer le fait
          (voir plus bas). Fermer via la croix, le clic sur le fond ou Échap laisse le badge tel
          quel, pour pouvoir relire plus tard sans perdre l'indicateur "nouveau". */}
      <button className="whats-new-trigger" title="Nouveautés" onClick={() => setOpen(true)}>
        <MegaphoneIcon />
        {unseen && <span className="whats-new-trigger-dot" aria-hidden="true" />}
      </button>
      {open && (
        <>
          <div
            className="whats-new-backdrop"
            onClick={() => setOpen(false)}
            onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); }}
          />
          <div className="whats-new-drawer">
            <div className="whats-new-header">
              <h2>Nouveautés</h2>
              <button className="whats-new-close" onClick={() => setOpen(false)} aria-label="Fermer">✕</button>
            </div>
            <div className="whats-new-body">
              <Suspense fallback={<p className="muted">Chargement…</p>}>
                <ReactMarkdown components={{ h2: VersionHeading }}>{changelogRaw}</ReactMarkdown>
              </Suspense>
            </div>
            {unseen && (
              <div className="whats-new-footer">
                <button
                  onClick={() => {
                    if (latestVersion) markSeenMutation.mutate(latestVersion);
                  }}
                  disabled={markSeenMutation.isPending}
                >
                  Marquer comme lu
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
}
